import { useEffect, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTypedFeatureFlag } from "@/hooks/use-feature-flag"
import { trackEvent } from "@/lib/tracking/analytics"
import { ArrowLeft, CreditCard, Lock } from "lucide-react"
import { toast } from "sonner"

import { useBillingStore } from "../store/billing-store"
import { CheckoutPaymentForm } from "./payment-form/checkout-payment-form"
import { PagarmeCheckoutForm } from "./payment-form/pagarme-checkout-form"
import { PlanSelector } from "./plan-selector"

import "./subscription-modal.css"

interface SubscriptionLockdownModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SubscriptionLockdownModal({ open, onOpenChange }: SubscriptionLockdownModalProps) {
  const { fetchSubscriptionStatus, selectedPlan, selectPlan } = useBillingStore()
  const usePagarme = useTypedFeatureFlag("use_pagarme_gateway", false)
  const [step, setStep] = useState<"select-plan" | "payment">("select-plan")
  const [paymentError, setPaymentError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setStep("select-plan")
      setPaymentError(null)
      fetchSubscriptionStatus()
    }
  }, [open, fetchSubscriptionStatus])

  useEffect(() => {
    if (selectedPlan && step === "select-plan") {
      trackEvent("Lockdown Select Plan", { plan: selectedPlan.name })
      setStep("payment")
    }
  }, [selectedPlan, step])

  const handlePaymentSuccess = async () => {
    await fetchSubscriptionStatus()
    toast.success("Assinatura realizada com sucesso!", { position: "top-right" })
    onOpenChange(false)
  }

  const handlePaymentError = (error: string) => {
    setPaymentError(error)
  }

  const handleBackToPlans = () => {
    selectPlan(null)
    setStep("select-plan")
    setPaymentError(null)
  }

  const handleCallConsultant = () => {
    window.open("https://wa.me/message/CNBXVAFBI26LA1", "_blank")
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="subscription-dialog-content max-h-[90vh] overflow-y-auto p-0 shadow-2xl sm:max-w-[900px]">
        <div className="from-primary/10 to-primary/5 border-b bg-gradient-to-r p-6">
          <DialogHeader className="mb-2">
            <Badge
              variant="outline"
              className="border-primary/20 bg-primary/10 text-primary mb-2 w-fit"
            >
              <Lock className="mr-1 h-3 w-3" />
              {step === "select-plan" ? "Escolha seu plano" : "Pagamento"}
            </Badge>
            <DialogTitle className="text-2xl font-bold">
              {step === "select-plan" ? "Seu periodo de teste terminou" : "Complete seus dados"}
            </DialogTitle>
            <DialogDescription className="mt-2 text-base">
              {step === "select-plan"
                ? "Para continuar usando o Speeke, escolha um dos planos abaixo."
                : "Preencha os dados para completar sua assinatura e continuar usando o sistema."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6">
          {step === "select-plan" ? (
            <>
              <PlanSelector />
              <div className="mt-6 flex justify-center">
                <Button
                  variant="outline"
                  className="flex w-[200px] items-center gap-2"
                  onClick={handleCallConsultant}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <g>
                      <path
                        fill="#25D366"
                        d="M12 2.04c-5.5 0-9.96 4.46-9.96 9.96 0 1.76.46 3.48 1.33 4.99L2 22l5.13-1.34c1.47.8 3.13 1.23 4.87 1.23 5.5 0 9.96-4.46 9.96-9.96S17.5 2.04 12 2.04zm0 17.92c-1.56 0-3.09-.41-4.41-1.19l-.31-.18-3.04.8.81-2.97-.2-.31A8.01 8.01 0 0 1 4 12c0-4.42 3.58-8 8-8s8 3.58 8 8-3.58 8-8 8z"
                      />
                      <path
                        fill="#25D366"
                        d="M17.47 14.37c-.25-.13-1.47-.73-1.7-.81-.23-.08-.4-.13-.57.13-.17.25-.65.81-.8.98-.15.17-.3.19-.55.06-.25-.13-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.15-.25-.02-.38.11-.5.12-.12.25-.3.37-.45.12-.15.16-.25.25-.41.08-.17.04-.32-.02-.45-.06-.13-.57-1.37-.78-1.88-.21-.5-.42-.43-.57-.44-.15-.01-.32-.01-.5-.01-.17 0-.45.06-.68.28-.23.22-.9.88-.9 2.14 0 1.26.92 2.48 1.05 2.65.13.17 1.81 2.77 4.39 3.77.61.21 1.09.34 1.46.44.61.16 1.16.14 1.6.09.49-.06 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.16-.48-.29z"
                      />
                    </g>
                  </svg>
                  Chamar consultor
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-6">
              {paymentError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                  {paymentError}
                </div>
              )}

              <div className="mb-4">
                <Button
                  variant="ghost"
                  onClick={handleBackToPlans}
                  className="flex h-8 items-center pl-0 text-sm"
                >
                  <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Voltar para os planos
                </Button>
              </div>

              {usePagarme && selectedPlan ? (
                <PagarmeCheckoutForm
                  selectedPlan={selectedPlan}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />
              ) : (
                <CheckoutPaymentForm
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />
              )}
            </div>
          )}

          <div className="text-muted-foreground mt-6 border-t pt-4 text-sm">
            <p className="flex items-center">
              <CreditCard className="text-primary mr-2 h-4 w-4" />
              {usePagarme
                ? "Pagamento seguro processado pela Pagar.me. Você pode cancelar a qualquer momento."
                : "Pagamento seguro processado pela Hotmart. Voce pode cancelar a qualquer momento."}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
